import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, Eye, Trash2, Calendar, BarChart3, Copy, FileDown, Pencil, Check, X as XIcon, Info, User, Filter, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import NotificationBell from '@/components/NotificationBell';
import PdfViewerModal from '@/components/PdfViewerModal';

export default function ProposalList() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newProposalName, setNewProposalName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Confirmation modals state
  const [deleteModal, setDeleteModal] = useState({ show: false, proposalId: null, proposalName: null });
  const [duplicateModal, setDuplicateModal] = useState({ show: false, proposalId: null, proposalName: null, newName: '' });
  const [editingNameId, setEditingNameId] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: null, name: '' });
  const [assignModal, setAssignModal] = useState({ show: false, proposalId: null, proposalName: null, currentAssignee: null });
  const [assignUsers, setAssignUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchProposals();
    // Load current user for role-based display
    const stored = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch(e) {} }
  }, []);

  // Reload proposals when returning to this page (navigation-based refresh)
  useEffect(() => {
    // Check on mount for reload flag
    const shouldReload = localStorage.getItem('reload_proposals');
    if (shouldReload) {
      console.log('[ProposalList] Reload flag detected - refreshing proposals');
      localStorage.removeItem('reload_proposals');
      fetchProposals();
    }

    // Handle storage events (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'reload_proposals' || e.key === 'reload_reference_data') {
        console.log('[ProposalList] Storage event detected - refreshing proposals');
        fetchProposals();
        localStorage.removeItem('reload_proposals');
      }
    };

    // Handle window focus (user returns to this tab)
    const handleFocus = () => {
      const shouldReload = localStorage.getItem('reload_proposals');
      if (shouldReload) {
        console.log('[ProposalList] Focus detected with reload flag - refreshing proposals');
        fetchProposals();
        localStorage.removeItem('reload_proposals');
      }
    };

    // Handle visibility change (tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const shouldReload = localStorage.getItem('reload_proposals');
        if (shouldReload) {
          console.log('[ProposalList] Page became visible with reload flag - refreshing proposals');
          fetchProposals();
          localStorage.removeItem('reload_proposals');
        }
      }
    };

    // Refetch proposals when page becomes visible (navigation return)
    const handlePageShow = (event) => {
      // Only refetch if page was loaded from cache (back/forward navigation)
      if (event.persisted || (performance.navigation && performance.navigation.type === 2)) {
        console.log('[ProposalList] Page restored from cache - refreshing proposals');
        fetchProposals();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Set reload flag when leaving this page (to refresh other pages)
  useEffect(() => {
    return () => {
      // Cleanup: Set flags for other pages to reload when user navigates away
      console.log('[ProposalList] Setting reload flags for Calculator and History on unmount');
      localStorage.setItem('reload_calculator', 'true');
      localStorage.setItem('reload_scenarios', 'true');
    };
  }, []);

  const isAdmin = ['admin', 'ceo', 'cfo', 'approver', 'manager'].includes(currentUser?.role);

  const filteredProposals = proposals.filter(p => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'draft') return !p.status || p.status === 'draft';
    if (statusFilter === 'pending') return p.status === 'pending_approval' || p.status === 'submitted' || p.status === 'pending_cfo_approval' || p.status === 'pending_ceo_approval';
    if (statusFilter === 'approved') return p.status === 'approved' || p.status === 'fully_approved';
    if (statusFilter === 'rejected') return p.status === 'rejected' || p.status === 'fully_denied';
    return true;
  });

  const statusCounts = {
    all: proposals.length,
    draft: proposals.filter(p => !p.status || p.status === 'draft').length,
    pending: proposals.filter(p => ['pending_approval','submitted','pending_cfo_approval','pending_ceo_approval'].includes(p.status)).length,
    approved: proposals.filter(p => ['approved','fully_approved'].includes(p.status)).length,
    rejected: proposals.filter(p => ['rejected','fully_denied'].includes(p.status)).length,
  };

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/collections');
      setProposals(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProposal = async () => {
    if (!newProposalName.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }
    try {
      const response = await api.post('/collections', { name: newProposalName });
      toast.success('Proposal created! Opening calculator...');
      setNewProposalName('');
      setShowNewModal(false);
      // Navigate to calculator with new proposal pre-selected
      navigate(`/calculator?proposal=${response.data.collection.id}`);
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error('Failed to create proposal');
    }
  };

  const handleDeleteProposal = async (proposalId, proposalName, proposalStatus) => {
    if (proposalStatus && proposalStatus !== 'draft') {
      toast.error(`Cannot delete ${proposalStatus} proposal. Only draft proposals can be deleted.`);
      return;
    }
    setDeleteModal({ show: true, proposalId, proposalName });
  };
  
  const confirmDelete = async () => {
    const { proposalId } = deleteModal;
    try {
      setProposals(prev => prev.filter(p => p.id !== proposalId));
      setDeleteModal({ show: false, proposalId: null, proposalName: null });
      await api.delete(`/collections/${proposalId}`);
      toast.success('Proposal deleted successfully');
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete proposal');
      await fetchProposals();
    }
  };

  const handleDuplicateProposal = async (proposalId, proposalName) => {
    setDuplicateModal({ show: true, proposalId, proposalName, newName: `${proposalName} (Copy)` });
  };
  
  const confirmDuplicate = async () => {
    const { proposalId, newName } = duplicateModal;
    if (!newName?.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }
    try {
      const response = await api.post(`/collections/${proposalId}/duplicate`, { name: newName.trim() });
      toast.success(`Proposal duplicated: ${response.data.collection.name}`);
      setDuplicateModal({ show: false, proposalId: null, proposalName: null, newName: '' });
      await fetchProposals();
    } catch (error) {
      console.error('Error duplicating proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate proposal');
    }
  };

  const handleExportPDF = async (proposalId) => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/collections/${proposalId}/export-pdf`, { responseType: 'arraybuffer' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const proposal = proposals.find(p => p.id === proposalId);
      const filename = `Proposal_${proposal?.name || proposalId}.pdf`;
      setPdfViewer({ show: true, url: blobUrl, name: proposal?.name || 'Proposal', blob, filename });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: null, name: '', blob: null, filename: '' });
  };

  const handleStartEditName = (e, proposalId, currentName) => {
    e.stopPropagation();
    setEditingNameId(proposalId);
    setEditNameValue(currentName);
  };

  const handleSaveCardName = async (e) => {
    e.stopPropagation();
    const trimmed = editNameValue.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await api.put(`/collections/${editingNameId}`, { name: trimmed });
      setProposals(prev => prev.map(p => p.id === editingNameId ? { ...p, name: trimmed } : p));
      setEditingNameId(null);
      toast.success('Proposal name updated');
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Failed to update name');
    }
  };

  const handleCancelEditName = (e) => {
    e.stopPropagation();
    setEditingNameId(null);
  };

  const handleOpenAssignModal = async (e, proposal) => {
    e.stopPropagation();
    setSelectedAssignee(proposal.assigned_to_id || '');
    setAssignModal({ show: true, proposalId: proposal.id, proposalName: proposal.name, currentAssignee: proposal.assigned_to_name });
    if (assignUsers.length === 0) {
      try {
        const res = await api.get('/admin/users?limit=100');
        setAssignUsers(res.data.users || []);
      } catch (err) {
        toast.error('Failed to load users');
      }
    }
  };

  const handleConfirmAssign = async () => {
    try {
      setAssigning(true);
      const res = await api.put(`/collections/${assignModal.proposalId}/assign`, {
        assigned_to_id: selectedAssignee || null
      });
      const assignedName = res.data.assigned_to_name;
      setProposals(prev => prev.map(p =>
        p.id === assignModal.proposalId
          ? { ...p, assigned_to_id: selectedAssignee || null, assigned_to_name: assignedName || null }
          : p
      ));
      toast.success(assignedName ? `Assigned to ${assignedName}` : 'Assignment removed');
      setAssignModal({ show: false, proposalId: null, proposalName: null, currentAssignee: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign proposal');
    } finally {
      setAssigning(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Proposals</h1>
              <p className="text-sm text-slate-400">Manage multi-service proposals</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowNewModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Proposal
              </button>
              <NotificationBell />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Workflow Guide */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg">
          <div className="flex gap-3">
            <Info className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-indigo-900 mb-2">📋 Proposal Workflow Guide</h3>
              <div className="text-xs text-indigo-800 space-y-1">
                <p><strong>1. Create a Proposal:</strong> Click "New Proposal" to start a multi-service collection</p>
                <p><strong>2. Add Services:</strong> Navigate to the Calculator and add multiple service calculations to your proposal</p>
                <p><strong>3. Review & Edit:</strong> View all calculations in the proposal, edit as needed</p>
                <p><strong>4. Submit for Approval:</strong> Once ready, submit the proposal for manager/executive review</p>
                <p><strong>5. Track Status:</strong> Monitor approval progress with status badges (Draft → Pending → Approved/Rejected)</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
          {[
            { key: 'all', label: 'All' },
            { key: 'draft', label: 'Draft' },
            { key: 'pending', label: 'Pending' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
          ].map(({ key, label }) => (
            <button
              key={key}
              data-testid={`status-filter-${key}`}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                statusFilter === key
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
              }`}
            >
              {label}
              {statusCounts[key] > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  statusFilter === key ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {statusCounts[key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="card p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-slate-600 mt-4">Loading proposals...</p>
          </div>
        ) : proposals.length === 0 ? (
          <div className="card p-12 text-center">
            <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Proposals Yet</h3>
            <p className="text-slate-600 mb-6">
              Create your first proposal collection to bundle multiple services together
            </p>
            <button
              onClick={() => setShowNewModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2 inline" />
              Create First Proposal
            </button>
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="card p-8 text-center">
            <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No proposals match the selected filter.</p>
            <button onClick={() => setStatusFilter('all')} className="mt-3 text-sm text-indigo-600 hover:underline">Clear filter</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProposals.map((proposal) => (
              <div
                key={proposal.id}
                className="card p-6 hover:shadow-xl transition-all cursor-pointer"
                onClick={() => navigate(`/proposal/${proposal.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <FolderOpen className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {editingNameId === proposal.id ? (
                        <div className="flex items-center gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            data-testid={`edit-card-name-input-${proposal.id}`}
                            type="text"
                            value={editNameValue}
                            onChange={(e) => setEditNameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCardName(e); if (e.key === 'Escape') handleCancelEditName(e); }}
                            className="font-semibold text-slate-900 border-b-2 border-indigo-500 bg-transparent outline-none flex-1 min-w-0"
                            autoFocus
                          />
                          <button data-testid={`save-card-name-${proposal.id}`} onClick={handleSaveCardName} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded flex-shrink-0">
                            <Check className="w-4 h-4" />
                          </button>
                          <button data-testid={`cancel-card-name-${proposal.id}`} onClick={handleCancelEditName} className="p-1 text-slate-400 hover:bg-slate-100 rounded flex-shrink-0">
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-1 min-w-0 group/name">
                          <h3 className="font-semibold text-slate-900 truncate">{proposal.name}</h3>
                          {(!proposal.status || proposal.status === 'draft') && (
                            <button
                              data-testid={`edit-card-name-btn-${proposal.id}`}
                              onClick={(e) => handleStartEditName(e, proposal.id, proposal.name)}
                              className="p-0.5 text-slate-300 opacity-0 group-hover/name:opacity-100 hover:text-indigo-600 rounded transition-all flex-shrink-0"
                              title="Edit name"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <BarChart3 className="w-4 h-4" />
                        <span>{proposal.scenario_count || 0} service{proposal.scenario_count !== 1 ? 's' : ''}</span>
                      </div>
                      {/* Status Badge — always visible including Draft */}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        (!proposal.status || proposal.status === 'draft') ? 'bg-slate-100 text-slate-600' :
                        proposal.status === 'submitted' || proposal.status === 'pending_approval' || proposal.status?.includes('pending') ? 'bg-yellow-100 text-yellow-800' :
                        proposal.status === 'fully_approved' || proposal.status === 'approved' ? 'bg-green-100 text-green-800' :
                        proposal.status === 'fully_denied' || proposal.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        proposal.status === 'partially_approved' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {(!proposal.status || proposal.status === 'draft') ? 'Draft' :
                         proposal.status === 'submitted' || proposal.status === 'pending_approval' ? 'Submitted' :
                         proposal.status?.includes('pending') ? 'Pending' :
                         proposal.status === 'fully_approved' || proposal.status === 'approved' ? 'Approved' :
                         proposal.status === 'fully_denied' || proposal.status === 'rejected' ? 'Denied' :
                         proposal.status === 'partially_approved' ? 'Partially Approved' :
                         proposal.status}
                      </span>
                      {/* Created By — shown for admins when it's a different user */}
                      {isAdmin && proposal.created_by_name && (
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <User className="w-3 h-3" />
                          {proposal.created_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportPDF(proposal.id, proposal.name);
                      }}
                      className="p-2 rounded-lg transition-colors text-blue-500 hover:bg-blue-50"
                      title="Export to PDF"
                    >
                      <FileDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateProposal(proposal.id, proposal.name);
                      }}
                      className="p-2 rounded-lg transition-colors text-green-500 hover:bg-green-50"
                      title="Duplicate proposal"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProposal(proposal.id, proposal.name, proposal.status);
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        proposal.status && proposal.status !== 'draft'
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-500 hover:bg-red-50'
                      }`}
                      title={proposal.status && proposal.status !== 'draft' 
                        ? 'Cannot delete submitted proposal' 
                        : 'Delete proposal'}
                      disabled={proposal.status && proposal.status !== 'draft'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>Created {formatDate(proposal.created_at)}</span>
                  </div>
                  {proposal.updated_at && proposal.updated_at !== proposal.created_at && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="w-4 h-4" />
                      <span>Updated {formatDate(proposal.updated_at)}</span>
                    </div>
                  )}
                  {/* Assigned To Badge */}
                  {proposal.assigned_to_name ? (
                    <div className="flex items-center gap-2 text-slate-600">
                      <UserCheck className="w-4 h-4 text-indigo-500" />
                      <span className="text-indigo-700 font-medium text-xs">Assigned: {proposal.assigned_to_name}</span>
                      {isAdmin && (
                        <button
                          data-testid={`assign-btn-${proposal.id}`}
                          onClick={(e) => handleOpenAssignModal(e, proposal)}
                          className="ml-auto text-xs text-indigo-500 hover:text-indigo-700 underline"
                        >
                          Change
                        </button>
                      )}
                    </div>
                  ) : isAdmin && (
                    <button
                      data-testid={`assign-btn-${proposal.id}`}
                      onClick={(e) => handleOpenAssignModal(e, proposal)}
                      className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-600 transition-colors"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      Assign to user
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                  {/* Edit button for drafts - opens calculator */}
                  {(!proposal.status || proposal.status === 'draft') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/calculator?proposal=${proposal.id}`);
                      }}
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Service
                    </button>
                  )}
                  
                  {/* View Details button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/proposal/${proposal.id}`);
                    }}
                    className={`btn-secondary flex items-center justify-center gap-2 ${
                      (!proposal.status || proposal.status === 'draft') ? 'flex-1' : 'w-full'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Proposal Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Create New Proposal</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Proposal Name
              </label>
              <input
                type="text"
                value={newProposalName}
                onChange={(e) => setNewProposalName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateProposal()}
                placeholder="e.g., Los Angeles County 2024 Q1"
                className="input-field w-full"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowNewModal(false);
                  setNewProposalName('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProposal}
                className="btn-primary"
                disabled={!newProposalName.trim()}
              >
                Create Proposal
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete "<strong>{deleteModal.proposalName}</strong>"? 
              All services will be unlinked but not deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteModal({ show: false, proposalId: null, proposalName: null })}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Proposal Modal */}
      {duplicateModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Duplicate Proposal</h3>
            <p className="text-slate-600 mb-2">Enter a name for the duplicated proposal:</p>
            <input
              type="text"
              value={duplicateModal.newName}
              onChange={(e) => setDuplicateModal({ ...duplicateModal, newName: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && confirmDuplicate()}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-6"
              autoFocus
              placeholder="Enter proposal name"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDuplicateModal({ show: false, proposalId: null, proposalName: null, newName: '' })}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDuplicate}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewer.show && (
        <PdfViewerModal
          url={pdfViewer.url}
          name={pdfViewer.name}
          blob={pdfViewer.blob}
          filename={pdfViewer.filename}
          onClose={closePdfViewer}
        />
      )}

      {/* Assign Proposal Modal */}
      {assignModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Assign Proposal</h3>
            <p className="text-sm text-slate-500 mb-4">
              Assign <strong>{assignModal.proposalName}</strong> to a team member
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Assign To</label>
              <select
                data-testid="assign-user-select"
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">— Unassigned —</option>
                {assignUsers.map(u => (
                  <option key={u.user_id || u.id} value={u.user_id || u.id}>
                    {u.name || u.email} ({u.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setAssignModal({ show: false, proposalId: null, proposalName: null, currentAssignee: null })}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-assign-btn"
                type="button"
                onClick={handleConfirmAssign}
                disabled={assigning}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 transition-colors text-sm"
              >
                {assigning ? 'Saving...' : 'Save Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
